// The report side of segments (PRD 137 §4.5, Wave 3): one segment filter on
// the P&L family, sales by item, the GL register and the journal entry list,
// beside the property filter, and ANDed with it server-side.
//
// Pure helpers only — the wire params, the URL param that keeps a filtered
// report shareable, the header label, a schedule's stored form, and the words
// for the two ways the api refuses a filter. No filter is always `null` and
// always sends nothing, so an unfiltered report's request is exactly what it
// was before segments existed.

import { BusinessApiError } from '../../../services/businessApiCommon';
import {
  ReportSegmentSelection,
  SEGMENT_FILTER_ERROR_CODES,
  SEGMENT_SLOTS,
  SegmentFilterParams,
  SegmentFilterWire,
  SegmentSlot,
} from '../../../types/segmentsApi';

/** One value in a report filter's list, in tree order (parents before their children). */
export interface ReportSegmentValueOption {
  id: string;
  /** The value's own name, indented by `depth` in the list. */
  name: string;
  /** "West › Denver" — what a label or header says. */
  label: string;
  parentId: string | null;
  depth: number;
  active: boolean;
}

/** An active dimension a report can filter by, with every value it has carried. */
export interface ReportSegmentDimension {
  slot: SegmentSlot;
  name: string;
  values: ReportSegmentValueOption[];
}

const asSlot = (value: unknown): SegmentSlot | null => {
  const slot = Number(value);
  return (SEGMENT_SLOTS as readonly number[]).includes(slot) ? (slot as SegmentSlot) : null;
};

/** A selection that filters nothing (no ids) is no filter. */
export function normalizeSegmentSelection(
  selection: ReportSegmentSelection | null | undefined
): ReportSegmentSelection | null {
  if (!selection) return null;
  if (selection.mode === 'ids' && selection.ids.length === 0) return null;
  return selection;
}

/**
 * GET query params for buildQuery. Ids go comma-joined, which the api reads
 * the same as `segment_value_ids[]`. Empty for no filter.
 */
export function segmentFilterQueryParams(
  selection: ReportSegmentSelection | null | undefined
): Record<string, string | undefined> {
  const filter = normalizeSegmentSelection(selection);
  if (!filter) return {};
  if (filter.mode === 'unassigned') {
    return { segment_slot: String(filter.slot), segment_unassigned: 'true' };
  }
  return { segment_slot: String(filter.slot), segment_value_ids: filter.ids.join(',') };
}

/** The same params for a POST body (`reports/email`). Empty for no filter. */
export function segmentFilterBodyParams(
  selection: ReportSegmentSelection | null | undefined
): SegmentFilterParams {
  const filter = normalizeSegmentSelection(selection);
  if (!filter) return {};
  if (filter.mode === 'unassigned') return { segment_slot: filter.slot, segment_unassigned: true };
  return { segment_slot: filter.slot, segment_value_ids: filter.ids };
}

// ---- URL param ------------------------------------------------------------
// One `segment` search param, like `properties`: "<slot>:unassigned" or
// "<slot>:<id>,<id>". Anything unreadable is no filter.

export const SEGMENT_URL_PARAM = 'segment';

export function parseSegmentParam(value: string | null): ReportSegmentSelection | null {
  if (!value) return null;
  const [slotPart, rest = ''] = value.split(':', 2);
  const slot = asSlot(slotPart);
  if (!slot) return null;
  if (rest === 'unassigned') return { slot, mode: 'unassigned' };
  const ids = rest
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
  return ids.length > 0 ? { slot, mode: 'ids', ids } : null;
}

/** Inverse of parseSegmentParam; undefined when the param should be absent. */
export function segmentParamValue(selection: ReportSegmentSelection | null | undefined): string | undefined {
  const filter = normalizeSegmentSelection(selection);
  if (!filter) return undefined;
  return filter.mode === 'unassigned' ? `${filter.slot}:unassigned` : `${filter.slot}:${filter.ids.join(',')}`;
}

// ---- Labels -----------------------------------------------------------------

/**
 * The report header's words for a filter: "Region: West, West › Denver" or
 * "Region: Unassigned only". Null for no filter, so a caller prints nothing.
 * Falls back to the echoed dimension name, then "Segment", when the
 * dimensions have not loaded.
 */
export function segmentSelectionLabel(
  selection: ReportSegmentSelection | null | undefined,
  dimensions: ReportSegmentDimension[],
  fallbackDimensionName?: string
): string | null {
  const filter = normalizeSegmentSelection(selection);
  if (!filter) return null;
  const dimension = dimensions.find(candidate => candidate.slot === filter.slot);
  const name = dimension?.name ?? fallbackDimensionName ?? 'Segment';
  if (filter.mode === 'unassigned') return `${name}: Unassigned only`;
  const labels = filter.ids.map(id => dimension?.values.find(value => value.id === id)?.label);
  if (labels.some(label => !label)) {
    return `${name}: ${filter.ids.length} ${filter.ids.length === 1 ? 'value' : 'values'}`;
  }
  return `${name}: ${labels.join(', ')}`;
}

// ---- Schedules --------------------------------------------------------------

export function segmentFilterWire(selection: ReportSegmentSelection | null | undefined): SegmentFilterWire | null {
  const filter = normalizeSegmentSelection(selection);
  if (!filter) return null;
  return filter.mode === 'unassigned'
    ? { slot: filter.slot, value_ids: [], unassigned: true }
    : { slot: filter.slot, value_ids: filter.ids, unassigned: false };
}

export function selectionFromWire(wire: SegmentFilterWire | null | undefined): ReportSegmentSelection | null {
  if (!wire) return null;
  const slot = asSlot(wire.slot);
  if (!slot) return null;
  if (wire.unassigned) return { slot, mode: 'unassigned' };
  return normalizeSegmentSelection({ slot, mode: 'ids', ids: wire.value_ids ?? [] });
}

/** Two selections that filter the same lines (id order does not matter). */
export function sameSegmentSelection(
  a: ReportSegmentSelection | null | undefined,
  b: ReportSegmentSelection | null | undefined
): boolean {
  const left = normalizeSegmentSelection(a);
  const right = normalizeSegmentSelection(b);
  if (!left || !right) return !left && !right;
  if (left.slot !== right.slot || left.mode !== right.mode) return false;
  if (left.mode === 'unassigned' || right.mode === 'unassigned') return true;
  const sorted = (ids: string[]) => ids.slice().sort().join(',');
  return sorted(left.ids) === sorted(right.ids);
}

// ---- Errors -----------------------------------------------------------------

/**
 * The api refuses a filter rather than drop it (a "Denver" P&L that ignored
 * Denver would print the whole business under its name): 400
 * invalid_segment_filter or 422 segment_not_set_up. Say which, and what to do;
 * any other error keeps the server's sentence, and a non-api failure the
 * caller's fallback.
 */
export function segmentAwareErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof BusinessApiError)) return fallback;
  if (error.code === SEGMENT_FILTER_ERROR_CODES.notSetUp) {
    return 'That segment is not set up for this business any more. Clear the segment filter and run the report again.';
  }
  if (error.code === SEGMENT_FILTER_ERROR_CODES.invalid) {
    return `The segment filter was refused: ${error.message}. Clear it or pick other values.`;
  }
  return error.message;
}
